/**
 * paty-fbt.js — "Comprados juntos frecuentemente".
 * Carga las recomendaciones por Section Rendering API (intent=complementary con
 * fallback intent=related), filtra por línea cosmética (data-linea) en cliente,
 * revela la sección si hay >=2 recos válidas, gestiona la selección/total y añade
 * todo al carrito abriendo el cart drawer. CSP-safe, sin handlers inline.
 * Mejora progresiva: sin JS la sección queda [hidden] y no aparece.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

function init(section) {
  const linea = section.dataset.linea;
  const handle = section.dataset.handle;
  const productId = section.dataset.productId;
  const currency = section.dataset.currency || 'EUR';
  const cardsHost = section.querySelector('[data-fbt-cards]');
  const totalEl = section.querySelector('[data-fbt-total]');
  const countLabel = section.querySelector('[data-fbt-count-label]');
  const addBtn = section.querySelector('[data-fbt-add]');
  const addLabel = section.querySelector('[data-fbt-add-label]');
  const errorEl = section.querySelector('[data-fbt-error]');
  const wasEl = section.querySelector('[data-fbt-was]');
  const wasAmountEl = section.querySelector('[data-fbt-was-amount]');
  const savingsEl = section.querySelector('[data-fbt-savings]');
  const noteEl = section.querySelector('[data-fbt-note]');
  const discountPct = parseInt(section.dataset.discountPct, 10) || 0;
  const minItems = parseInt(section.dataset.minItems, 10) || 3;
  const freeshipCents = parseInt(section.dataset.freeshipCents, 10) || 0;
  const tFreeship = section.dataset.tFreeship || '';
  const tAdd = section.dataset.tAdd || 'Añadir set al carrito';
  const tError = section.dataset.tError || 'No se pudo agregar al carrito.';
  const tSavings = section.dataset.tSavings || '';
  const tNudge = section.dataset.tNudge || '';

  let money;
  try { money = new Intl.NumberFormat('es-ES', { style: 'currency', currency }); }
  catch { money = new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }); }

  // La metafield de línea puede venir como JSON (lista) — extraer texto plano para buscar.
  let searchTerm = linea || '';
  try {
    const parsed = JSON.parse(linea);
    if (Array.isArray(parsed)) searchTerm = parsed.join(' ');
    else if (typeof parsed === 'string') searchTerm = parsed;
  } catch { /* línea es texto plano */ }

  let started = false;
  let wired = false;

  async function fetchCards(url) {
    try {
      const res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return [];
      const html = await res.text();
      const tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      return Array.from(tpl.content.querySelectorAll('[data-fbt-card]'));
    } catch {
      return [];
    }
  }

  const recosUrl = (intent) =>
    `${ROOT}recommendations/products?section_id=paty-fbt-recos&product_id=${encodeURIComponent(productId)}&intent=${intent}&limit=10`;
  const searchUrl = () =>
    `${ROOT}search?section_id=paty-fbt-search&type=product&q=${encodeURIComponent(searchTerm)}`;

  function pickValid(cards, seen, out) {
    for (const card of cards) {
      if (out.length >= 3) break;
      const h = card.dataset.handle;
      if (!h || h === handle || seen.has(h)) continue;
      if (card.dataset.linea !== linea) continue;
      seen.add(h);
      out.push(card);
    }
  }

  async function load() {
    if (started) return;
    started = true;
    const seen = new Set([handle]);
    const chosen = [];
    pickValid(await fetchCards(recosUrl('complementary')), seen, chosen);
    if (chosen.length < 2) pickValid(await fetchCards(recosUrl('related')), seen, chosen);
    // Fallback robusto: las recomendaciones pueden venir vacías (cold-start de sesión);
    // la búsqueda por el texto de la línea encuentra los hermanos de forma fiable.
    if (chosen.length < 2 && searchTerm) pickValid(await fetchCards(searchUrl()), seen, chosen);
    if (chosen.length < 2) { section.remove(); return; }
    chosen.forEach((c) => cardsHost.appendChild(c));
    section.hidden = false;
    wire();
    recompute();
  }

  function selectedCards() {
    return Array.from(section.querySelectorAll('[data-fbt-card]')).filter((c) => {
      const cb = c.querySelector('[data-fbt-checkbox]');
      return cb && cb.checked;
    });
  }

  function recompute() {
    const sel = selectedCards();
    const totalCents = sel.reduce((s, c) => s + (parseInt(c.dataset.priceCents, 10) || 0), 0);
    const n = sel.length;
    const discountApplies = discountPct > 0 && n >= minItems;

    if (discountApplies) {
      const discounted = Math.round((totalCents * (100 - discountPct)) / 100);
      if (wasEl) { if (wasAmountEl) wasAmountEl.textContent = money.format(totalCents / 100); else wasEl.textContent = money.format(totalCents / 100); wasEl.hidden = false; }
      totalEl.textContent = money.format(discounted / 100);
      if (savingsEl) {
        let savingsText = tSavings
          .replaceAll('__AMOUNT__', money.format((totalCents - discounted) / 100))
          .replaceAll('__PCT__', String(discountPct));
        // "+ envío gratis" solo si el total CON descuento alcanza el umbral real de la tienda.
        if (freeshipCents > 0 && tFreeship && discounted >= freeshipCents) {
          savingsText += ` ${tFreeship}`;
        }
        savingsEl.textContent = savingsText;
        savingsEl.hidden = false;
      }
      if (noteEl) noteEl.hidden = false;
    } else {
      if (wasEl) wasEl.hidden = true;
      totalEl.textContent = money.format(totalCents / 100);
      if (savingsEl) {
        if (discountPct > 0 && n > 0 && n < minItems) {
          savingsEl.textContent = tNudge
            .replaceAll('__N__', String(minItems - n))
            .replaceAll('__PCT__', String(discountPct));
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }
      if (noteEl) noteEl.hidden = true;
    }

    countLabel.textContent = `Total del set (${n} ${n === 1 ? 'producto' : 'productos'})`;
    addLabel.textContent = `${tAdd} (${n})`;
    addBtn.disabled = n === 0 || addBtn.getAttribute('aria-busy') === 'true';
  }

  function wire() {
    if (wired) return;
    wired = true;
    section.addEventListener('change', (e) => {
      if (e.target && e.target.matches && e.target.matches('[data-fbt-checkbox]')) recompute();
    });
    addBtn.addEventListener('click', addAll);
  }

  async function addAll() {
    const sel = selectedCards();
    const items = sel.map((c) => ({ id: c.dataset.variantId, quantity: 1 }));
    if (!items.length) return;
    errorEl.hidden = true;
    addBtn.setAttribute('aria-busy', 'true');
    addBtn.disabled = true;

    // Pedir el render de las secciones del carrito para refrescar el drawer sin recargar.
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    addBtn.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    }));

    try {
      const res = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items, sections: Array.from(sectionIds).join(',') }),
      });
      const data = await res.json();

      if (data.status) {
        deferred.resolve({ detail: { didError: true, source: 'paty-fbt' } });
        errorEl.textContent = data.description || tError;
        errorEl.hidden = false;
        return;
      }

      const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'paty-fbt', sections: data.sections, didError: false },
      });

      // Abrir el cart drawer (independiente del ajuste auto_open_cart_drawer).
      const drawer = document.getElementById('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    } catch (err) {
      if (typeof deferred.reject === 'function') deferred.reject(err);
      errorEl.textContent = tError;
      errorEl.hidden = false;
      console.warn('[paty-fbt] add to cart failed:', err);
    } finally {
      addBtn.removeAttribute('aria-busy');
      recompute();
    }
  }

  // Carga diferida: idle tras 'load', o al primer scroll, lo que ocurra antes.
  function defer() {
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
    idle(load);
  }
  if (document.readyState === 'complete') defer();
  else window.addEventListener('load', defer, { once: true });
  window.addEventListener('scroll', load, { once: true, passive: true });
}

const el = document.getElementById('paty-fbt');
if (el) init(el);
